import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Typography,
  CircularProgress,
  Button,
  IconButton,
  Menu,
  MenuItem,
  TextField,
} from '@mui/material';
import {
  getMisReport,
  getMisReportColumns,
  getMisReportDownload,
} from 'redux/actions/ReportsAction';
import { fetchCompanyList } from 'redux/actions/companyAction';
import { getServiceCity } from 'redux/actions/UserServiceCity';
import CommonButton from 'components/commonButton/CommonButton';
import CommonTable from 'components/commonTable/CommonTable';
import Loader from 'components/loader/Loader';
import CommonFilter from 'components/commonFilter/CommonFilter';
import CommonDateRangePicker from 'components/commonDateRangePicker/CommonDateRangePicker';
import BillingFilter from 'pages/operation/billing/billingFlow/billingFilter/BillingFilter';
import { getDateFormat } from 'utils/utils';
import {
  getReportPresetsApi,
  createReportPresetApi,
  updateReportPresetApi,
  deleteReportPresetApi,
} from 'services/ReportsService';
import { debounce, isArray } from 'lodash';
import { misFilterData, misFilterOptions } from './MisReportData';
import { requestBookingReportApi } from 'services/BookingReportService';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CloseIcon from '@mui/icons-material/Close';

function Report() {
  const dispatch = useDispatch();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [status, setStatus] = useState([]);
  const [location, setLocation] = useState([]);
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const [dateFrom, setDateFrom] = useState(getDateFormat(thirtyDaysAgo, 'reservationDateFormat'));
  const [dateTo, setDateTo] = useState(getDateFormat(new Date(), 'reservationDateFormat'));
  const [isRequestingReport, setIsRequestingReport] = useState(false);
  const [selectedReportColumns, setSelectedReportColumns] = useState([]);
  const [isColumnsModalOpen, setIsColumnsModalOpen] = useState(false);
  // Saved column presets (pipeline item 2, second pass 2026-09-23; user wording: preset).
  // Tenant-wide, keyed by report type; nothing is remembered per browser
  // (user decision). Adding or updating one happens in its own dialog with
  // its own name and column ticks, so the request ticks are never touched.
  const [presets, setPresets] = useState([]);
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [appliedPresetId, setAppliedPresetId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [presetBusy, setPresetBusy] = useState(false);
  // null | { preset: null } (new) | { preset } (update)
  const [presetEditor, setPresetEditor] = useState(null);
  const [editorName, setEditorName] = useState('');
  const [editorColumns, setEditorColumns] = useState([]);
  const [selectedReportFormat, setSelectedReportFormat] = useState('');

  const misLoader = useSelector((state) => state?.getMisList?.loading);
  const misDataList = useSelector((state) => state?.getMisList?.misList?.data);
  const reportColumnsData = useSelector((state) => state?.getMisList?.reportColumns);
  const reportColumns = Array.isArray(reportColumnsData) ? reportColumnsData : [];
  const reportColumnsLoading = useSelector((state) => state?.getMisList?.reportColumnsLoading);

  useEffect(() => {
    if (reportColumns.length > 0) {
      setSelectedReportColumns(reportColumns);
    }
  }, [reportColumns]);

  // New Filter added
  const [billingFilter, setBillingFilter] = useState({
    companyName: {},
    city: {},
  });

  let filterData = {
    search: '',
  };
  useEffect(() => {
    dispatch(
      fetchCompanyList(filterData, {
        searchType: 'globalSearch',
        loader: 'backDropLoader',
      }),
    );
    dispatch(getServiceCity());
    dispatch(getMisReportColumns());
  }, []);

  const getReportColumnLabel = (column) =>
    typeof column === 'string'
      ? column
      : column?.label ||
        column?.displayName ||
        column?.name ||
        column?.columnName ||
        column?.key ||
        'Unnamed column';

  const getReportColumnValue = (column) =>
    typeof column === 'string'
      ? column
      : column?.value || column?.key || column?.columnName || column?.name || column?.id;

  const isReportColumnSelected = (column) =>
    selectedReportColumns.some(
      (selectedColumn) => getReportColumnValue(selectedColumn) === getReportColumnValue(column),
    );

  const handleReportColumnToggle = (column) => {
    if (isReportColumnSelected(column)) {
      setSelectedReportColumns((currentColumns) =>
        currentColumns.filter(
          (selectedColumn) => getReportColumnValue(selectedColumn) !== getReportColumnValue(column),
        ),
      );
      return;
    }

    setSelectedReportColumns((currentColumns) => [...currentColumns, column]);
  };

  const companyTableData = useSelector((state) => state?.userCompany?.companyList?.body);
  const locationOptions = useSelector((state) => state?.userServiceCity?.userServiceCity?.data);

  const companyOption =
    companyTableData?.length > 0 &&
    companyTableData?.map((val) => {
      return { value: val?.id, label: val.name };
    });

  const cityOption =
    locationOptions?.length > 0 &&
    locationOptions?.map((val) => {
      return { value: val?.id, label: val.name };
    });

  const handleFilter = (name, newValue) => {
    setBillingFilter((prevState) => ({
      ...prevState,
      [name]: newValue,
    }));

    if (name === 'companyName') {
      const bookingReportPayloads = {
        ...revenueReportPayload,
        companyId: newValue?.value,
        page: 0,
      };
      dispatch(getMisReport(bookingReportPayloads));
    } else if (name === 'city') {
      const bookingReportPayloads = {
        ...revenueReportPayload,
        city: newValue?.value,
        page: 0,
      };
      dispatch(getMisReport(bookingReportPayloads));
    }
  };

  //company Auto suggestion
  const onHandleAutoSuggestion = debounce((event, value, fieldName) => {
    if (event?.type === 'change') {
      if (fieldName === 'companyName' && value) {
        dispatch(
          fetchCompanyList(
            { ...filterData, search: value },
            { searchType: 'globalSearch', loader: 'backDropLoader' },
          ),
        );
      } else {
        dispatch(
          fetchCompanyList(filterData, {
            searchType: 'globalSearch',
            loader: 'backDropLoader',
          }),
        );
      }
    }
  }, 500);

  let revenueReportPayload = {
    page: page,
    pageSize: rowsPerPage,
    status: status,
    location: location,
    dateTo: dateTo,
    dateFrom: dateFrom,
    city: billingFilter?.city?.value,
    report: true,
    companyId: billingFilter?.companyName?.value,
  };

  useEffect(() => {
    dispatch(getMisReport(revenueReportPayload));
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    dispatch(getMisReport({ ...revenueReportPayload, page: newPage }));
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
    dispatch(
      getMisReport({
        ...revenueReportPayload,
        page: 0,
        pageSize: parseInt(event.target.value),
      }),
    );
  };

  //Date Filter
  const onSelectDateRange = (newValue) => {
    setPage(0);
    if (newValue === null || newValue === 0) {
      setDateFrom('');
      setDateTo('');
      revenueReportPayload = {
        ...revenueReportPayload,
        dateFrom: '',
        dateTo: '',
        page: 0,
      };
    } else {
      setDateFrom(getDateFormat(newValue[0], 'reservationDateFormat'));
      setDateTo(getDateFormat(newValue[1], 'reservationDateFormat'));
      revenueReportPayload = {
        ...revenueReportPayload,
        dateFrom: getDateFormat(newValue[0], 'reservationDateFormat'),
        dateTo: getDateFormat(newValue[1], 'reservationDateFormat'),
        page: 0,
      };
    }
    dispatch(getMisReport(revenueReportPayload));
  };

  const onFilterChange = (val) => {
    const defaultStatus = ['1', '2'];
    const defaultLocation = ['rental', 'airport', 'outStation'];
    const filterStatus = val.filter((status) => {
      return defaultStatus.includes(status);
    });

    const filterLocations = val.filter((DutyType) => {
      return defaultLocation.includes(DutyType);
    });

    setLocation(filterLocations);
    setStatus(filterStatus);
    setPage(0);

    const bookingReportPayloads = {
      ...revenueReportPayload,
      status: filterStatus?.toString(),
      location: filterLocations?.toString(),
      page: 0,
    };
    dispatch(getMisReport(bookingReportPayloads));
  };

  // ---Reset Filter---
  const onRestFilter = () => {
    dispatch(
      getMisReport({
        ...revenueReportPayload,
        status: '',
        location: '',
        page: 0,
      }),
    );
    dispatch(
      getMisReport({
        ...revenueReportPayload,
        status: '',
        location: '',
        page: 0,
      }),
    );
    setStatus([]);
    setLocation([]);
    setPage(0);
  };

  const onClick = () => {
    const reservationPayload = {
      ...revenueReportPayload,
      export: true,
      report: false,
    };
    dispatch(getMisReportDownload(reservationPayload));
  };

  const REPORT_TYPE = 'B2B_TRIP_TRANSACTION_REPORT';

  const selectedColumnKeys = () => selectedReportColumns.map(getReportColumnValue).filter(Boolean);

  const sameColumnSet = (a = [], b = []) => a.length === b.length && a.every((k) => b.includes(k));

  const loadPresets = async () => {
    setPresetsLoading(true);
    try {
      const res = await getReportPresetsApi(REPORT_TYPE);
      setPresets(res?.success && Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      setPresets([]);
    } finally {
      setPresetsLoading(false);
    }
  };

  useEffect(() => {
    if (isColumnsModalOpen) {
      loadPresets();
      setConfirmDeleteId(null);
      setPresetEditor(null);
    }
  }, [isColumnsModalOpen]);

  // A change of ticks that no longer matches the applied preset drops the
  // highlight, so "Applied" always means "these exact columns".
  useEffect(() => {
    if (!appliedPresetId) return;
    const applied = presets.find((p) => p.id === appliedPresetId);
    if (!applied || !sameColumnSet(applied.columns, selectedColumnKeys())) {
      setAppliedPresetId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReportColumns]);

  const applyPreset = (preset) => {
    const keys = new Set(preset.columns || []);
    setSelectedReportColumns(
      reportColumns.filter((column) => keys.has(getReportColumnValue(column))),
    );
    setAppliedPresetId(preset.id);
    setConfirmDeleteId(null);
  };

  // Clear unticks only the applied preset's columns; anything ticked by
  // hand on top of it stays.
  const clearPreset = (preset) => {
    const keys = new Set(preset.columns || []);
    setSelectedReportColumns((current) =>
      current.filter((column) => !keys.has(getReportColumnValue(column))),
    );
    setAppliedPresetId(null);
  };

  const openPresetEditor = (preset = null) => {
    setConfirmDeleteId(null);
    setEditorName(preset?.name ?? '');
    setEditorColumns(preset?.columns ? [...preset.columns] : []);
    setPresetEditor({ preset });
  };

  const closePresetEditor = () => {
    if (presetBusy) return;
    setPresetEditor(null);
  };

  const isEditorColumnSelected = (column) => editorColumns.includes(getReportColumnValue(column));

  const toggleEditorColumn = (column) => {
    const key = getReportColumnValue(column);
    setEditorColumns((current) =>
      current.includes(key) ? current.filter((k) => k !== key) : [...current, key],
    );
  };

  const toggleEditorSelectAll = () => {
    setEditorColumns(
      editorColumns.length === reportColumns.length
        ? []
        : reportColumns.map(getReportColumnValue).filter(Boolean),
    );
  };

  const savePresetEditor = async (e) => {
    e?.preventDefault?.();
    const name = editorName.trim().replace(/\s+/g, ' ');
    if (!name) return toast.error('Give the preset a name');
    // Keep the report's own column order rather than the order of ticking.
    const columns = reportColumns
      .map(getReportColumnValue)
      .filter((key) => key && editorColumns.includes(key));
    if (columns.length === 0) return toast.error('Tick at least one column');
    const editing = presetEditor?.preset;
    setPresetBusy(true);
    try {
      const res = editing
        ? await updateReportPresetApi(editing.id, { name, columns })
        : await createReportPresetApi({
            reportType: REPORT_TYPE,
            name,
            columns,
          });
      if (res?.success) {
        toast.success(
          editing ? `Updated "${name}"` : `Saved "${name}" with ${columns.length} columns`,
        );
        setPresetEditor(null);
        await loadPresets();
        // An update to the applied preset re-applies it so the ticks match.
        if (editing && appliedPresetId === editing.id) {
          applyPreset({ id: editing.id, columns });
        }
      } else {
        toast.error(
          res?.data?.message || res?.message || `Failed to ${editing ? 'update' : 'save'} preset`,
        );
      }
    } finally {
      setPresetBusy(false);
    }
  };

  const deletePreset = async (preset) => {
    setPresetBusy(true);
    try {
      const res = await deleteReportPresetApi(preset.id);
      if (res?.success) {
        toast.success(`Deleted "${preset.name}"`);
        if (appliedPresetId === preset.id) clearPreset(preset);
        setConfirmDeleteId(null);
        await loadPresets();
      } else {
        toast.error(res?.data?.message || res?.message || 'Failed to delete preset');
      }
    } finally {
      setPresetBusy(false);
    }
  };

  const onRequestReport = async () => {
    setIsRequestingReport(true);
    try {
      const payload = {
        startDate: dateFrom,
        endDate: dateTo,
        reportType: 'B2B_TRIP_TRANSACTION_REPORT',
        reportFormate: selectedReportFormat,
        columns: selectedReportColumns.map(getReportColumnValue).filter(Boolean),
        filters: {
          location: revenueReportPayload?.city,
          type:
            revenueReportPayload?.location && isArray(revenueReportPayload?.location)
              ? revenueReportPayload?.location.join(',')
              : '',
          companyId: revenueReportPayload?.companyId,
          parentCompany: revenueReportPayload?.parentCompany,
          status:
            revenueReportPayload?.status && isArray(revenueReportPayload?.status)
              ? revenueReportPayload?.status.join(',')
              : '',
        },
      };
      const response = await requestBookingReportApi(payload);
      if (response.success) {
        toast.success('Report request submitted successfully');
        setIsColumnsModalOpen(false);
        navigate('/report-generate');
      } else {
        toast.error(response?.message || 'Failed to request report');
      }
    } catch (error) {
      toast.error('Error requesting report');
    } finally {
      setIsRequestingReport(false);
    }
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSelectFormat = (format) => {
    setSelectedReportFormat(format);
    handleClose();
    setIsColumnsModalOpen(true);
  };

  return (
    <Box>
      <Box className="justifyBetween pb10">
        <Typography variant="body1" className="colorPrimary">
          Transaction Data
        </Typography>
      </Box>

      <Grid container spacing={1} sx={{ width: '100%' }}>
        <Grid
          container
          gap={2}
          sx={{ width: '100%' }}
          flexWrap={{ xs: 'wrap', sm: 'wrap', md: 'nowrap', lg: 'nowrap' }}
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
        >
          <Grid
            item
            container
            sx={{ width: '100%', marginLeft: '10px', marginBottom: '10px' }}
            gap={2}
          >
            {misFilterOptions(companyOption, cityOption) &&
              misFilterOptions(companyOption, cityOption)?.map((formData, index) => (
                <Grid item lg={5} md={12} xs={12} key={index} className="misAutoComplete">
                  <BillingFilter
                    billingFilterValue={billingFilter}
                    setBillingFilter={setBillingFilter}
                    handleFilter={handleFilter}
                    formData={formData}
                    onHandleAutoSuggestion={onHandleAutoSuggestion}
                  />
                </Grid>
              ))}
          </Grid>
          <Grid container item>
            <Grid
              item
              lg={12}
              xs={12}
              className="justifyEnd"
              style={{ marginTop: '10px', marginLeft: '10px' }}
            >
              <CommonDateRangePicker
                onSelectDateRange={onSelectDateRange}
                dateFrom={dateFrom}
                dateTo={dateTo}
              />
              <CommonFilter
                defaultStatus={status}
                filterdata={misFilterData}
                onFilterChange={onFilterChange}
                onRestFilter={onRestFilter}
                defaultLocation={location}
              />
            </Grid>
            <div
              style={{
                display: 'flex',
                gap: '20px',
                marginTop: '10px',
                alignItems: 'center',
                justifyContent: 'flex-end',
                width: '100%',
                minWidth: 0,
              }}
            >
              <Button
                onClick={handleClick}
                disabled={isRequestingReport}
                sx={{
                  backgroundColor: '#1976d2',
                  borderRadius: '8px',
                  color: '#fff',
                  textTransform: 'none',
                  paddingX: 2,
                  paddingY: 1,
                  '&:hover': {
                    backgroundColor: '#1565c0',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#9e9e9e',
                    color: '#fff',
                  },
                }}
              >
                {isRequestingReport ? (
                  <CircularProgress size={20} sx={{ color: '#fff' }} />
                ) : (
                  'Request Report'
                )}
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                  sx: { width: anchorEl ? anchorEl.offsetWidth : 'auto' }, // match button width
                }}
              >
                <MenuItem
                  onClick={() => handleSelectFormat('csv')}
                  sx={{ width: '100%', fontSize: '14px' }}
                >
                  Excel/CSV
                </MenuItem>
                <MenuItem
                  onClick={() => handleSelectFormat('pdf')}
                  sx={{ width: '100%', fontSize: '14px' }}
                >
                  PDF
                </MenuItem>
              </Menu>
              <CommonButton
                name="Send Report to mail"
                btnClass="btnSm btnYellow"
                onClick={onClick}
              />
            </div>
          </Grid>
        </Grid>

        {misLoader ? (
          <Loader height={'60vh'} />
        ) : (
          <Box
            sx={{
              width: '100%',
              overflowX: 'auto',
              maxHeight: '80vh',
              overflowY: 'auto',
              marginLeft: '10px',
            }}
          >
            <CommonTable
              data={misDataList}
              page={page}
              rowsPerPage={rowsPerPage}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              minWidth={true}
              isClickable={false}
            />
          </Box>
        )}
      </Grid>
      <Dialog
        open={isColumnsModalOpen}
        onClose={() => setIsColumnsModalOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Select report columns
          <IconButton
            aria-label="Close"
            onClick={() => setIsColumnsModalOpen(false)}
            sx={{
              position: 'absolute',
              right: 12,
              top: 10,
              color: 'text.secondary',
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select at least one column to request a report.
          </Typography>
          {reportColumnsLoading ? (
            <Box className="justifyCenter p20">
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              {/* Boxed panel: heading, Add button and helper line stay put; only
                  the list of presets scrolls (about three rows). */}
              <Box
                sx={{
                  mb: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    pt: 1.5,
                    pb: 1,
                    bgcolor: 'grey.50',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        color: 'text.secondary',
                      }}
                    >
                      Saved column presets
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={presetBusy}
                      onClick={() => openPresetEditor(null)}
                    >
                      + Add new preset
                    </Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    A Saved Column Preset is a saved set of columns. Apply one, adjust if needed,
                    then request. Clear removes its ticks.
                  </Typography>
                </Box>

                {presetsLoading ? (
                  <Box className="justifyCenter p10">
                    <CircularProgress size={18} />
                  </Box>
                ) : presets.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
                    No column presets saved yet for this report.
                  </Typography>
                ) : (
                  <Box sx={{ maxHeight: 232, overflowY: 'auto' }}>
                    {presets.map((preset) => {
                      const isApplied = appliedPresetId === preset.id;
                      const confirming = confirmDeleteId === preset.id;
                      return (
                        <Box
                          key={preset.id}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 1,
                            px: 1.5,
                            py: 1,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            bgcolor: isApplied ? 'success.light' : 'transparent',
                            '&:last-of-type': { borderBottom: 0 },
                          }}
                        >
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {preset.name}
                              {isApplied && (
                                <Typography
                                  component="span"
                                  variant="caption"
                                  sx={{
                                    ml: 1,
                                    color: 'success.dark',
                                    fontWeight: 600,
                                  }}
                                >
                                  APPLIED
                                </Typography>
                              )}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {(preset.columns || []).length} of {reportColumns.length} columns
                            </Typography>
                          </Box>
                          {confirming ? (
                            <Box
                              sx={{
                                display: 'flex',
                                gap: 1,
                                alignItems: 'center',
                                flexWrap: 'wrap',
                              }}
                            >
                              <Typography variant="caption">
                                Delete the column preset &quot;{preset.name}
                                &quot;?
                              </Typography>
                              <Button
                                size="small"
                                color="error"
                                variant="contained"
                                disabled={presetBusy}
                                onClick={() => deletePreset(preset)}
                              >
                                Delete
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={presetBusy}
                                onClick={() => setConfirmDeleteId(null)}
                              >
                                Cancel
                              </Button>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              {isApplied ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="success"
                                  disabled={presetBusy}
                                  onClick={() => clearPreset(preset)}
                                >
                                  Clear
                                </Button>
                              ) : (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={presetBusy}
                                  onClick={() => applyPreset(preset)}
                                >
                                  Apply
                                </Button>
                              )}
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={presetBusy}
                                onClick={() => openPresetEditor(preset)}
                              >
                                Update
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                disabled={presetBusy}
                                onClick={() => setConfirmDeleteId(preset.id)}
                              >
                                Delete
                              </Button>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
                {presets.length > 0 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      px: 2,
                      py: 0.5,
                      bgcolor: 'grey.50',
                      borderTop: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {presets.length} saved preset{presets.length > 1 ? 's' : ''}
                    {presets.length > 3 ? ' · scroll for more' : ''}
                  </Typography>
                )}
              </Box>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      reportColumns.length > 0 &&
                      selectedReportColumns.length === reportColumns.length
                    }
                    indeterminate={
                      selectedReportColumns.length > 0 &&
                      selectedReportColumns.length < reportColumns.length
                    }
                    onChange={() =>
                      setSelectedReportColumns(
                        selectedReportColumns.length === reportColumns.length ? [] : reportColumns,
                      )
                    }
                  />
                }
                label="Select all"
              />
              <Box
                sx={{
                  maxHeight: 420,
                  overflowY: 'auto',
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                }}
              >
                {reportColumns.map((column) => (
                  <FormControlLabel
                    key={getReportColumnValue(column)}
                    control={
                      <Checkbox
                        checked={isReportColumnSelected(column)}
                        onChange={() => handleReportColumnToggle(column)}
                      />
                    }
                    label={getReportColumnLabel(column)}
                    sx={{ mr: 0 }}
                  />
                ))}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={onRequestReport}
            disabled={
              isRequestingReport || reportColumnsLoading || selectedReportColumns.length === 0
            }
          >
            {isRequestingReport ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create / update a saved column preset: its own name and ticks. */}
      <Dialog open={Boolean(presetEditor)} onClose={closePresetEditor} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={savePresetEditor}>
          <DialogTitle>
            {presetEditor?.preset ? 'Update column preset' : 'New column preset'}
            <IconButton
              aria-label="Close"
              onClick={closePresetEditor}
              sx={{
                position: 'absolute',
                right: 12,
                top: 10,
                color: 'text.secondary',
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <TextField
              size="small"
              label="Preset name"
              required
              value={editorName}
              onChange={(e) => setEditorName(e.target.value)}
              inputProps={{ maxLength: 60 }}
              autoFocus
              fullWidth
              sx={{ mb: 2, maxWidth: 420 }}
            />
            <Typography
              variant="subtitle2"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: 'text.secondary',
              }}
            >
              Columns in this preset
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    reportColumns.length > 0 && editorColumns.length === reportColumns.length
                  }
                  indeterminate={
                    editorColumns.length > 0 && editorColumns.length < reportColumns.length
                  }
                  onChange={toggleEditorSelectAll}
                />
              }
              label="Select all"
            />
            <Box
              sx={{
                maxHeight: 360,
                overflowY: 'auto',
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              }}
            >
              {reportColumns.map((column) => (
                <FormControlLabel
                  key={getReportColumnValue(column)}
                  control={
                    <Checkbox
                      checked={isEditorColumnSelected(column)}
                      onChange={() => toggleEditorColumn(column)}
                    />
                  }
                  label={getReportColumnLabel(column)}
                  sx={{ mr: 0 }}
                />
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
              {editorColumns.length} of {reportColumns.length} columns
            </Typography>
            <Button variant="outlined" disabled={presetBusy} onClick={closePresetEditor}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={presetBusy}>
              {presetBusy ? (
                <CircularProgress size={20} sx={{ color: '#fff' }} />
              ) : presetEditor?.preset ? (
                'Save changes'
              ) : (
                'Save preset'
              )}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
}

export default Report;
